import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { PageHeader } from "../components/PageHeader";
import { IngredientForm } from "../components/IngredientForm";
import { ALL_MEAL_TYPES } from "../lib/utils";
import { normalizeRecipeSteps } from "../lib/recipeSteps";
import type { Ingredient, Recipe, RecipeIngredient, Store, StoreSection } from "../types";

interface RecipeDetail extends Recipe {
  ingredients: RecipeIngredient[];
}

interface StoreDetail {
  store: Store;
  sections: StoreSection[];
}

export default function RecipeDetailPage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const recipeQ = useQuery({
    queryKey: ["recipe", id],
    queryFn: () => api.get<{ recipe: RecipeDetail }>(`/api/recipes/${id}`),
  });
  const ingredientsQ = useQuery({
    queryKey: ["ingredients"],
    queryFn: () => api.get<{ ingredients: Ingredient[] }>("/api/ingredients"),
  });
  const storesQ = useQuery({
    queryKey: ["stores"],
    queryFn: () => api.get<{ stores: Store[] }>("/api/stores"),
  });
  const storeIds = storesQ.data?.stores.map((s) => s.id) ?? [];
  const storeDetailsQs = useQueries({
    queries: storeIds.map((sid) => ({
      queryKey: ["store", sid],
      queryFn: () => api.get<StoreDetail>(`/api/stores/${sid}`),
    })),
  });
  const sectionsByStore = useMemo(() => {
    const map = new Map<string, StoreSection[]>();
    for (const q of storeDetailsQs) {
      if (q.data) map.set(q.data.store.id, q.data.sections);
    }
    return map;
  }, [storeDetailsQs.map((q) => q.dataUpdatedAt).join("|")]);

  const [name, setName] = useState("");
  const [servings, setServings] = useState(2);
  const [steps, setSteps] = useState<string[]>([]);
  const [mealTypes, setMealTypes] = useState<string[]>([]);
  const [items, setItems] = useState<{ ingredientId: string; quantity: number | "" }[]>([]);
  const [hydrated, setHydrated] = useState(false);
  // newIngredient určuje, kam případně vložit nově vytvořenou surovinu:
  //  - { mode: "append" }       → přidá nový řádek se surovinou na konec
  //  - { mode: "assign", idx }  → nastaví surovinu do existujícího řádku (z dropdownu „Vytvořit novou…“).
  const [newIngredient, setNewIngredient] = useState<
    null | { mode: "append" } | { mode: "assign"; idx: number }
  >(null);

  useEffect(() => {
    if (recipeQ.data && !hydrated) {
      const r = recipeQ.data.recipe;
      setName(r.name);
      setServings(r.servings);
      setSteps(normalizeRecipeSteps(r.instructions));
      setMealTypes(r.mealTypes ?? []);
      setItems(r.ingredients.map((ri) => ({ ingredientId: ri.ingredientId, quantity: ri.quantity })));
      setHydrated(true);
    }
  }, [recipeQ.data, hydrated]);

  const save = useMutation({
    mutationFn: () =>
      api.patch(`/api/recipes/${id}`, {
        name: name.trim(),
        servings: Number(servings) || 1,
        steps: normalizeRecipeSteps(steps),
        mealTypes,
        ingredients: items
          .filter((it) => it.ingredientId && it.quantity && Number(it.quantity) > 0)
          .map((it) => ({ ingredientId: it.ingredientId, quantity: Number(it.quantity) })),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["recipe", id] });
      qc.invalidateQueries({ queryKey: ["recipes"] });
      navigate("/recepty");
    },
  });
  const del = useMutation({
    mutationFn: () => api.delete(`/api/recipes/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["recipes"] });
      navigate("/recepty");
    },
  });

  if (!recipeQ.data) return <p className="text-slate-500">Načítám…</p>;

  const ingredientList = ingredientsQ.data?.ingredients ?? [];

  return (
    <div>
      <Link to="/recepty" className="text-sm text-slate-500 hover:underline">
        ← Zpět na recepty
      </Link>
      <PageHeader
        icon="🍳"
        title={recipeQ.data.recipe.name}
        actions={
          <>
            <button className="btn-primary flex items-center gap-1" onClick={() => save.mutate()} disabled={save.isPending}>
              <span aria-hidden>💾</span>
              <span>Uložit změny</span>
            </button>
            <button
              className="btn-danger flex items-center gap-1"
              onClick={() => {
                if (confirm("Opravdu smazat tento recept?")) del.mutate();
              }}
            >
              <span aria-hidden>🗑️</span>
              <span>Smazat recept</span>
            </button>
          </>
        }
      />

      <div className="grid gap-6 md:grid-cols-3">
        <div className="md:col-span-1 space-y-4">
          <div className="card card-body">
            <label className="label">Název</label>
            <input className="input mb-3" value={name} onChange={(e) => setName(e.target.value)} />
            <label className="label">Porce</label>
            <input
              className="input"
              type="number"
              min={1}
              step={1}
              value={servings}
              onChange={(e) => setServings(Number(e.target.value))}
            />
            <p className="text-xs text-slate-500 mt-2">Suroviny se škálují podle počtu porcí.</p>
          </div>

          <div className="card card-body">
            <label className="label flex items-center gap-1">
              <span aria-hidden>🍽️</span>
              <span>Vhodné pro</span>
            </label>
            <div className="space-y-1">
              {ALL_MEAL_TYPES.map((mt) => (
                <label key={mt.id} className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    className="w-4 h-4"
                    checked={mealTypes.includes(mt.id)}
                    onChange={(e) =>
                      setMealTypes((arr) =>
                        e.target.checked ? [...arr, mt.id] : arr.filter((x) => x !== mt.id)
                      )
                    }
                  />
                  <span>{mt.label}</span>
                </label>
              ))}
            </div>
            <p className="text-xs text-slate-500 mt-2">
              {mealTypes.length === 0
                ? "Nic nezaškrtnuto → recept je univerzální (nabídne se pro jakýkoli chod)."
                : "Recept se v jídelníčku přednostně nabídne pro vybrané chody."}
            </p>
          </div>

          <div className="card card-body">
            <div className="flex items-center justify-between mb-3">
              <label className="label mb-0 flex items-center gap-1">
                <span aria-hidden>📋</span>
                <span>Postup</span>
              </label>
              <button
                type="button"
                className="btn-secondary text-xs flex items-center gap-1"
                onClick={() => setSteps((arr) => [...arr, ""])}
              >
                <span aria-hidden>➕</span>
                <span>Přidat krok</span>
              </button>
            </div>
            {steps.length === 0 ? (
              <p className="text-sm text-slate-500">
                Zatím žádné kroky.{" "}
                <button type="button" className="underline text-brand-700" onClick={() => setSteps([""])}>
                  Přidat první krok
                </button>
              </p>
            ) : (
              <ol className="space-y-2 list-none">
                {steps.map((step, idx) => (
                  <li key={idx} className="flex gap-2 items-start">
                    <span className="text-sm font-medium text-slate-500 w-6 pt-2 shrink-0 text-right">
                      {idx + 1}.
                    </span>
                    <textarea
                      className="input flex-1 min-h-[60px] text-sm"
                      value={step}
                      placeholder={`Krok ${idx + 1}…`}
                      onChange={(e) =>
                        setSteps((arr) => arr.map((s, i) => (i === idx ? e.target.value : s)))
                      }
                    />
                    <button
                      type="button"
                      className="btn-ghost text-red-600 shrink-0"
                      onClick={() => setSteps((arr) => arr.filter((_, i) => i !== idx))}
                      aria-label={`Odebrat krok ${idx + 1}`}
                      title="Odebrat krok"
                    >
                      ✕
                    </button>
                  </li>
                ))}
              </ol>
            )}
          </div>
        </div>

        <div className="md:col-span-2 card card-body">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-semibold flex items-center gap-2">
              <span aria-hidden>🥕</span>
              <span>Suroviny</span>
            </h2>
            <div className="flex gap-2">
              <button
                className="btn-ghost text-sm flex items-center gap-1"
                onClick={() => setNewIngredient({ mode: "append" })}
                title="Vytvořit novou surovinu a přidat ji do receptu"
              >
                <span aria-hidden>✨</span>
                <span>Nová surovina</span>
              </button>
              <button
                className="btn-secondary text-sm flex items-center gap-1"
                onClick={() =>
                  setItems((arr) => [...arr, { ingredientId: ingredientList[0]?.id ?? "", quantity: "" }])
                }
              >
                <span aria-hidden>➕</span>
                <span>Přidat surovinu</span>
              </button>
            </div>
          </div>
          {items.length === 0 ? (
            <p className="text-sm text-slate-500">Zatím žádné suroviny. Klikněte na „Přidat surovinu“.</p>
          ) : (
            <div className="space-y-2">
              {items.map((it, idx) => {
                const ingredient = ingredientList.find((i) => i.id === it.ingredientId);
                return (
                  <div key={idx} className="flex gap-2 items-center">
                    <select
                      className="input flex-1"
                      value={it.ingredientId}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === "__new__") {
                          setNewIngredient({ mode: "assign", idx });
                          return;
                        }
                        setItems((arr) => arr.map((x, i) => (i === idx ? { ...x, ingredientId: val } : x)));
                      }}
                    >
                      <option value="">— vyberte surovinu —</option>
                      {ingredientList.map((i) => (
                        <option key={i.id} value={i.id}>
                          {i.name} ({i.unit})
                        </option>
                      ))}
                      <option value="__new__">+ Vytvořit novou surovinu…</option>
                    </select>
                    <div className="flex items-center gap-1">
                      <input
                        className="input w-24"
                        type="number"
                        step="0.01"
                        min={0}
                        value={it.quantity}
                        onChange={(e) =>
                          setItems((arr) =>
                            arr.map((x, i) => (i === idx ? { ...x, quantity: e.target.value === "" ? "" : Number(e.target.value) } : x))
                          )
                        }
                      />
                      <span className="text-sm text-slate-500 w-10">{ingredient?.unit ?? ""}</span>
                    </div>
                    <button
                      className="btn-ghost text-red-600"
                      onClick={() => setItems((arr) => arr.filter((_, i) => i !== idx))}
                      aria-label="Odebrat"
                    >
                      ✕
                    </button>
                  </div>
                );
              })}
            </div>
          )}
          {ingredientList.length === 0 && (
            <p className="text-xs text-slate-500 mt-3">
              Žádné suroviny nejsou v katalogu.{" "}
              <button
                type="button"
                className="underline text-brand-700"
                onClick={() => setNewIngredient({ mode: "append" })}
              >
                Vytvořit první surovinu
              </button>{" "}
              nebo přejít do <Link to="/suroviny" className="underline">katalogu</Link>.
            </p>
          )}
        </div>
      </div>

      {newIngredient && (
        <IngredientForm
          ingredient={null}
          stores={storesQ.data?.stores ?? []}
          sectionsByStore={sectionsByStore}
          onClose={() => setNewIngredient(null)}
          onCreated={(newId) => {
            // Po vytvoření suroviny ji rovnou navážeme na recept podle režimu otevření.
            if (newIngredient.mode === "assign") {
              const targetIdx = newIngredient.idx;
              setItems((arr) =>
                arr.map((x, i) => (i === targetIdx ? { ...x, ingredientId: newId } : x))
              );
            } else {
              setItems((arr) => [...arr, { ingredientId: newId, quantity: "" }]);
            }
          }}
        />
      )}
    </div>
  );
}
